import {
  InternalType,
  PackageNameEnum,
  PermissionEnum,
  Slug,
  UserLanguage,
} from '@electricjs/core_entity-client';

import { Division } from './divisions';
import { HRISIntegration, OrganizationAddOn } from './organizations';
import { OrganizationPackage } from './packages';

export enum OrganizationUserRoleSlugs {
  HUB_USER = 'hub_user',
  SUPER_ADMIN = 'super_admin',
  PRODUCT_SUPPORT = 'product_support',
}

export const RoleDisplayMapping = {
  [OrganizationUserRoleSlugs.HUB_USER]: {
    displayName: 'Employee',
    description:
      'Employee Access and submit tickets in the Electric User Hub, our support center',
  },
  [OrganizationUserRoleSlugs.SUPER_ADMIN]: {
    displayName: 'Admin',
    description:
      'Admin Access to Electric User Hub and Electric IT Management, our admin tool',
  },
  [OrganizationUserRoleSlugs.PRODUCT_SUPPORT]: {
    displayName: 'Product Support',
    description:
      'Product Support access to Electric User Hub and Electric IT Management',
  },
};

export type OrganizationUserRoles = Array<{
  id: string;
  slug: OrganizationUserRoleSlugs;
}>;

export type Role = {
  id: string;
  slug: OrganizationUserRoleSlugs;
  displayName: string;
  description: string;
};

export type RoleOption = {
  label: string;
  value: OrganizationUserRoleSlugs;
  description: string;
};

export type LicenseOption = {
  label: string;
  organizationPackageId: string;
  value: PackageNameEnum;
  description: string;
  licenseCount: number | null;
  availableLicenseCount: number | null;
  isCurrent: boolean;
  isDisabled: boolean;
};

export type UserOrganization = {
  employeeId?: string;
  roles: OrganizationUserRoles;
  permissions: PermissionEnum[];
  // Limited to the divisions the user has been granted access to.
  divisions?: Division[];
  // Only populated via /v1/users/me/identities - /v1/users/me doesn't return it.
  name?: string;
};

// An org (from /v1/users/me/identities) the user must choose between at
// login instead of auto-selecting the first one (NERV-5621).
export type OrgSelectionChoice = Pick<
  UserOrganization,
  'roles' | 'employeeId' | 'name'
> & {
  id: string;
};

type CallerIdentity = {
  id: string;
  email: string;
  userLanguage?: UserLanguage;
};

export type User = CallerIdentity & {
  auth0Id?: string;
};

export type CallerIdentityWithOrganizations = CallerIdentity & {
  organizations: {
    [key: string]: UserOrganization;
  };
};

export enum ElectricPermission {
  ORGANIZATION_CREATE = 'organizations:create',
  APPLICATION_CREATE = 'applications:create',
  APPLICATION_READ = 'applications:read',
  APPLICATION_UPDATE = 'applications:update',
  APPLICATION_DELETE = 'applications:delete',
  ORDERS_READ = 'orders:read',
  ORDERS_UPDATE = 'orders:update',
  ORDERS_ISSUE_REFUND = 'orders:issue_refund',
  PRODUCTS_READ = 'products:read',
  PRODUCTS_UPDATE = 'products:update',
}

export type LoggedUser = {
  id?: string;
  name: string;
  email: string;
  accessToken?: string;
  userLanguage?: UserLanguage;
  employeeId?: string;
  employeeFirstName?: string;
  employeeLastName?: string;
  employeeJobTitle?: string;
  organizationId?: string;
  // True only when organizationId was chosen to disambiguate a shared
  // auth0_id across multiple distinct user records (NERV-5621, e.g.
  // multi-tenant isolved). Used to attach X-Disambiguate-Organization-Id so
  // backend calls can disambiguate the same way; not needed for normal org
  // switching.
  requiresOrgDisambiguationHeader?: boolean;
  organizationInternalType?: InternalType;
  organizationName?: string;
  organizationPackageId?: string;
  organizationEntitlementSlugs?: Slug[];
  organizationAddOns?: OrganizationAddOn[];
  organizationSalesforceId?: string;
  organizationUserRoles?: OrganizationUserRoles;
  organizationHrisIntegration?: HRISIntegration;
  allUserOrganizationRoles?: CallerIdentityWithOrganizations['organizations'];
  electricPermissions?: ElectricPermission[];
  adp_sso_login?: boolean;
  organizationPackages?: OrganizationPackage[];
};
